import type { TranslationKey } from '@/i18n-key';
import type { CompensationChange, CompensationChangeField } from '@/types/compensation';

import { formatCurrency, formatDecimal, formatPercent } from '@/utils/numberFormat';

/** What `summarizeChange` returns: a translation key under `compensation.history.changes` — the
 * field names double as leaf keys there, see `CompensationChangeField` — plus the interpolation
 * values the corresponding string needs (`before`/`after` always; `name` for a per-line change;
 * `label` for a per-level multiplier). */
export interface ChangeSummary {
    key: TranslationKey;
    values: {
        before: string | null;
        after: string | null;
        name?: string;
        label?: string;
    };
}

/** Fields that describe one employee's line: their `CompensationChange.employeeId` names the
 * person, which the caller resolves to a display name for the `name` interpolation. */
const LINE_CHANGE_FIELDS = new Set<CompensationChange['field']>([
    'lineSalary',
    'linePercent',
    'lineLock',
    'lineNote',
]);

export interface SummarizeChangeOptions {
    change: CompensationChange;
    /** Resolves a `CompensationChange.employeeId` to a display name for a per-line change, and a
     * `levelId` to a translated label for `levelMultiplier`. Both come from the envelope's own
     * `computed.lines`/`levels`, which the caller already has and this pure utility should not
     * need to fetch again. */
    resolveEmployeeName?: (employeeId: string) => string | undefined;
    resolveLevelLabel?: (levelId: string) => string | undefined;
}

/**
 * Turns one `CompensationChange` into the translation key and values `HistoryModal` hands `t()`. The field name is already the exact leaf key under `compensation.history.changes`
 * (`CompensationChangeField`), so no separate lookup table is needed to go from one to the other.
 */
export const summarizeChange = ({
    change,
    resolveEmployeeName,
    resolveLevelLabel,
}: SummarizeChangeOptions): ChangeSummary => {
    const key: TranslationKey = `compensation.history.changes.${change.field}`;
    const values: ChangeSummary['values'] = { before: change.before, after: change.after };

    if (LINE_CHANGE_FIELDS.has(change.field) && change.employeeId) {
        values.name = resolveEmployeeName?.(change.employeeId) ?? change.employeeId;
    }

    if (change.field === 'levelMultiplier' && change.levelId) {
        values.label = resolveLevelLabel?.(change.levelId) ?? change.levelId;
    }

    return { key, values };
};

/**
 * One side of a change, ready to render: either a piece of text already formatted for the reader's
 * locale, or a translation key the caller renders itself (an enumeration value). `null` means "nothing on this side" — the version
 * where a note was first written has no before, and the modal draws no struck-through blank.
 */
export type ChangeValue = { kind: 'text'; text: string } | { kind: 'key'; key: TranslationKey };

/** Enumeration values arrive from the wire as their own names (`AllCompany`, `Score`); nobody
 * should read those. */
const ENUM_KEYS: Partial<Record<CompensationChangeField, Record<string, TranslationKey>>> = {
    audienceType: {
        AllCompany: 'compensation.history.audienceAll',
        SpecificTeams: 'compensation.history.audienceSpecific',
    },
    budgetInputMode: {
        Amount: 'compensation.budgetCard.modeAmount',
        PayrollPercent: 'compensation.budgetCard.modePercent',
    },
    distributionMode: {
        Level: 'compensation.strategyCard.modeLevel',
        Score: 'compensation.strategyCard.modeScore',
    },
    status: {
        Draft: 'compensation.status.draft',
        Closed: 'compensation.status.closed',
    },
    lineLock: {
        true: 'compensation.history.values.locked',
        false: 'compensation.history.values.unlocked',
    },
};

/** The same enumerations read on a company-wide envelope, where the whole audience is the company
 * rather than the owner's team. Only the values that read differently are listed. */
const COMPANY_WIDE_ENUM_KEYS: typeof ENUM_KEYS = {
    audienceType: {
        AllCompany: 'compensation.history.audienceAllCompany',
    },
};

/** Fields whose stored value is a number, and the unit it is a number of. */
const NUMERIC_FIELDS: Partial<Record<CompensationChangeField, 'money' | 'percent' | 'decimal'>> = {
    budget: 'money',
    lineSalary: 'money',
    linePercent: 'percent',
    scoreThreshold: 'decimal',
    scoreSpread: 'decimal',
    levelMultiplier: 'decimal',
};

export interface DescribeChangeValueOptions {
    field: CompensationChangeField;
    /** The raw value as the version stored it. */
    value: string | null;
    language: string;
    /** Names the whole audience "the company" rather than "the team". */
    isCompanyWide?: boolean;
}

/**
 * Turns the raw string a version stored into what the history should show. A salary is written
 * `65000` in the journal because a journal has to stay readable in ten years without a formatter;
 * a reader comparing it with the table it came from expects `65 000 $`.
 */
export const describeChangeValue = ({
    field,
    value,
    language,
    isCompanyWide = false,
}: DescribeChangeValueOptions): ChangeValue | null => {
    if (value === null || value.trim() === '') {
        return null;
    }

    const enumKey =
        (isCompanyWide ? COMPANY_WIDE_ENUM_KEYS[field]?.[value] : undefined) ??
        ENUM_KEYS[field]?.[value];
    if (enumKey) {
        return { kind: 'key', key: enumKey };
    }

    const unit = NUMERIC_FIELDS[field];
    const parsed = Number(value);

    if (!unit || !Number.isFinite(parsed)) {
        return { kind: 'text', text: value };
    }

    if (unit === 'money') {
        return { kind: 'text', text: formatCurrency(parsed, language) };
    }

    if (unit === 'percent') {
        return { kind: 'text', text: formatPercent(parsed, language, { fractionDigits: 2 }) };
    }

    return { kind: 'text', text: formatDecimal(parsed, language) };
};
